import { produce } from 'immer';
import { useCallback } from 'react';
import { NodeStateUpdateType } from 'flow-builder/types/state';
import { getChildEndX, getChildEndY, getChildOffset, getChildStartX, getChildStartY, } from 'flow-builder/utils/canvas/calculateChildPosition/calculateChildPosition';
import { updateRelayedConnector } from 'flow-builder/utils/canvas/updateRelayed/updateRelayed';
export var NodeMoveType;
(function (NodeMoveType) {
    NodeMoveType["Drag"] = "drag";
    NodeMoveType["Start"] = "start";
    NodeMoveType["End"] = "end";
})(NodeMoveType || (NodeMoveType = {}));
const moveRelayedNode = (id, movement, relayedNodesRef) => {
    const relayedNode = relayedNodesRef.current[id];
    if (!relayedNode) {
        return;
    }
    const { ref } = relayedNode;
    ref.position.x += movement.x;
    ref.position.y += movement.y;
};
const moveRelayedConnector = (id, movement, relayedConnectorsRef) => {
    const relayedConnector = relayedConnectorsRef.current[id];
    if (!relayedConnector) {
        return;
    }
    const { ref } = relayedConnector;
    ref.position.x += movement.x;
    ref.position.y += movement.y;
};
const updateConnection = (changes, id, args, props) => {
    const { type, nodes, movement } = args;
    const { relayedConnectorsRef, relayedNodesRef } = props;
    const { relations } = changes;
    const { height, parentId, connections: dependencies } = relations[id];
    const isDragging = type !== NodeMoveType.End;
    const { ref } = relayedNodesRef.current[id];
    const pageX = ref.position.x;
    const pageY = ref.position.y;
    if (parentId) {
        const isParentUnmoved = nodes.indexOf(parentId) === -1;
        const relayedConnector = relayedConnectorsRef.current[id];
        const { ref } = relayedConnector;
        relayedConnector.isDragging = isDragging;
        if (isParentUnmoved) {
            const position = {
                endX: getChildEndX(pageX),
                endY: getChildEndY(pageY),
            };
            updateRelayedConnector(id, position, relayedConnectorsRef);
            changes.connections[id]['endX'] = position.endX;
            changes.connections[id]['endY'] = position.endY;
        }
        else {
            const { startX: stateStartX, startY: stateStartY, offset, } = changes.connections[id];
            const differenceX = ref.position.x - stateStartX - offset.x;
            const differenceY = ref.position.y - stateStartY - offset.y;
            changes.connections[id]['startX'] += differenceX;
            changes.connections[id]['startY'] += differenceY;
            changes.connections[id]['endX'] += differenceX;
            changes.connections[id]['endY'] += differenceY;
            moveRelayedConnector(id, movement, relayedConnectorsRef);
        }
    }
    const amountOfOuts = dependencies.length;
    dependencies.forEach((dependency, index) => {
        const connectionId = dependency.connection || dependency.id;
        const isChild = dependency.connection != null;
        const isChildUnmoved = isChild && nodes.indexOf(dependency.connection) === -1;
        const relayedConnector = relayedConnectorsRef.current[connectionId];
        if (!relayedConnector) {
            return;
        }
        relayedConnector.isDragging = isDragging;
        if (!isChild || isChildUnmoved) {
            const startX = getChildStartX(pageX);
            const startY = getChildStartY(pageY);
            const offset = getChildOffset(amountOfOuts, index + 1);
            const position = Object.assign({ startX,
                startY }, (!isChild && {
                offset: {
                    x: offset,
                    y: height,
                },
                endX: startX + offset,
                endY: startY + 200,
            }));
            changes.connections[connectionId]['startX'] = position.startX;
            changes.connections[connectionId]['startY'] = position.startY;
            if (!isChild) {
                changes.connections[connectionId]['endX'] =
                    position.endX;
                changes.connections[connectionId]['endY'] =
                    position.endY;
                changes.connections[connectionId]['offset'] =
                    position.offset;
            }
            updateRelayedConnector(connectionId, position, relayedConnectorsRef);
        }
    });
};
const updateRelation = (changes, id, args, props) => {
    const { movement } = args;
    const { relayedNodesRef } = props;
    const relayedNode = relayedNodesRef.current[id];
    if (!relayedNode) {
        return;
    }
    moveRelayedNode(id, movement, relayedNodesRef);
    const { ref } = relayedNode;
    const position = ref.position;
    changes.relations[id]['pageX'] = position.x;
    changes.relations[id]['pageY'] = position.y;
};
const createNodeStateUpdater = (args, props) => produce((nodeState) => {
    const { changes, relations } = nodeState;
    const { nodes } = args;
    changes.unshift(NodeStateUpdateType.NodeMove);
    nodes.forEach((id) => {
        /*
         * If it's a `NodeVisualConnection` that's highlighted,
         * then it won't exist as a relation, therefore we don't need to move it.
         */
        if (!relations[id]) {
            return;
        }
        updateRelation(nodeState, id, args, props);
        updateConnection(nodeState, id, args, props);
    });
});
export const useNodeMove = ({ nodeState, setNodeState, relayedConnectorsRef, relayedNodesRef, }) => useCallback((args) => {
    const props = {
        nodeState,
        setNodeState,
        relayedConnectorsRef,
        relayedNodesRef,
    };
    const { type } = args;
    const updateNodeState = createNodeStateUpdater(args, props);
    if (type === NodeMoveType.End) {
        setNodeState(updateNodeState);
    }
    else {
        updateNodeState(nodeState);
    }
}, [nodeState, setNodeState, relayedConnectorsRef, relayedNodesRef]);
